import { Popup } from 'react-leaflet';
import { useNavigate } from 'react-router';
import { useMemo } from 'react';

import CloseMapControl from '@/components/CloseMapControl';
import Map from '@/components/Map';
import WrappedMarker from '@/components/WrappedMarker';
import { NEAREST_PHOTOS_COUNT } from '@/constants';
import { usePhotos } from '@/hooks/usePhotos';
import useRoutePhoto from '@/hooks/useRoutePhoto';
import { Photo } from '@/lib/Photo';
import { Photos } from '@/lib/Photos';

import { CustomZoomSlider } from './CustomZoomSlider';
import { LocateUserControl } from './LocateUserControl';
import { MinimapControl } from './MinimapControl';
import { ResetViewControl } from './ResetViewControl';

type PhotoWithGeo = Photo & { geo: NonNullable<Photo['geo']> };

export const MapPane = () => {
  const navigate = useNavigate();
  const { photos: photoData, selectedPhoto: selectedPhotoData } = usePhotos();
  
  // Convert PhotoData to Photo instances
  const photoInstances = useMemo(() => photoData.map(data => Photo.fromApi(data)), [photoData]);
  const photos = useMemo(() => new Photos(photoInstances), [photoInstances]);
  const selectedPhoto = selectedPhotoData ? Photo.fromApi(selectedPhotoData) : null;
  const { photo: routePhoto } = useRoutePhoto();
  const photo = routePhoto ?? selectedPhoto;

  if ((!photo && !photos) ||
    (photo && !photo.hasGeo())) {
    return null;
  }

  const onMapCloseClick = () => { navigate(-1); }

  // For every point in the set, we add the point diagonally opposite,
  // through the centre point. This means the combined set of points
  // will have a bounding box with the centre point in... the centre.
  const centredMapBounds = (photos: PhotoWithGeo[]) => {
    const [center, ...rest] = photos;
    const latlng = (p: PhotoWithGeo) => [p.geo.lat, p.geo.lng] as [number, number];
    const centerPoint = latlng(center);

    if (rest.length == 0) {
      return [centerPoint];
    }

    const basicPoints = rest.map(latlng);
    const reflectedPoints = basicPoints.map((p) => {
      return [centerPoint[0] + (centerPoint[0] - p[0]), centerPoint[1] + (centerPoint[1] - p[1])] as [number, number];
    });

    return [...basicPoints, ...reflectedPoints];
  }

  const bounds = photo ?
    centredMapBounds([photo, ...photos.nearestPhotos(photo as PhotoWithGeo, NEAREST_PHOTOS_COUNT)]) :
    centredMapBounds(photos.withGeo);

  return (
    <div className="relative w-full h-[calc(100vh-6rem)] flex flex-col bg-white rounded-lg overflow-hidden">
      <div className="flex-1 text-white" style={{ backgroundColor: 'rgba(0, 0, 0, 0.9)' }}>
        <Map
          geo={photo?.geo}
          bounds={bounds}
          extraControls={(
            <>
              <CloseMapControl className="leaflet-top leaflet-right" onClick={() => onMapCloseClick()} />
              <CustomZoomSlider sliderPosition="leftside" />
              <MinimapControl position="topright" className="mr-12" />
              <ResetViewControl position="bottomright" bounds={bounds} center={(photo?.geo?.lat && photo?.geo?.lng) ? [photo?.geo?.lat, photo?.geo?.lng] : undefined} className="mr-12" />
              <LocateUserControl position="bottomright" />
            </>
          )}
        >
          {photos.withGeo.map((p) => (
            <WrappedMarker
              key={p.id}
              id={p.id}
              position={[p.geo.lat, p.geo.lng]}
              isMarkerForRoute={p.id === routePhoto?.id}
              markerAction={() => navigate(p.url())}
            // eventHandlers={{
            //   click: () => { onPhotoClick(p); }
            // }}
            >
              <Popup>
                <p>{p.title}</p>
                <p>{p.geo.name}, {p.geo.region} {/* , {p.geo.prov}, {p.geo.state} */}</p>
              </Popup>
            </WrappedMarker>
          ))}
        </Map>
      </div>
    </div>
  );
};
